import React, { useMemo, useState } from "react";

export default function EmployeesEfficiencyChart({ routes = [] }) {
  const [sortKey, setSortKey] = useState("efficiency"); // efficiency | total | driver
  const [groupBy, setGroupBy] = useState("route"); // route | driver | loaders
  const [useRealScale, setUseRealScale] = useState(false); // реальное соотношение высот

  // === Формируем статистику ===
  const stats = useMemo(() => {
    return routes.map((r) => {
      const receipts = r.receipts || [];
      const total = receipts.length;
      const delivered = receipts.filter((x) => x.status === "Закрыта").length;
      const failed = receipts.filter((x) => x.status === "Не доставлено").length;
      const open = receipts.filter((x) => x.status === "Открыта").length;
      const efficiency = total ? (delivered / total) * 100 : 0;

      return {
        id: r.id,
        route: r.route_number ? `#${r.route_number}` : "—",
        date: r.date || r.route_date || null,
        driver: r.driver || "—",
        loader1: r.loader1 || "",
        loader2: r.loader2 || "",
        delivered,
        failed,
        open,
        total,
        efficiency,
      };
    });
  }, [routes]);

  const maxOrders = stats.length ? Math.max(...stats.map((r) => r.total)) : 1;

  // === Сортировка ===
  const sorted = useMemo(() => {
    const arr = [...stats];

    switch (sortKey) {
      case "date":
        arr.sort((a, b) => {
          const da = a.date ? new Date(a.date).getTime() : 0;
          const db = b.date ? new Date(b.date).getTime() : 0;
          return da - db; // от новых к старым
        });
        break;

      case "total":
        arr.sort((a, b) => b.total - a.total);
        break;

      case "driver":
        arr.sort((a, b) => a.driver.localeCompare(b.driver, "ru"));
        break;

      default:
        arr.sort((a, b) => b.efficiency - a.efficiency);
    }

    return arr;
  }, [stats, sortKey]);

  // === Группировка ===
  const grouped = useMemo(() => {
    if (groupBy === "driver") {
      const map = {};
      sorted.forEach((r) => {
        if (!map[r.driver]) map[r.driver] = [];
        map[r.driver].push(r);
      });
      return Object.entries(map).map(([key, items]) => ({
        group: key,
        items,
      }));
    }
    if (groupBy === "loaders") {
      const map = {};
      sorted.forEach((r) => {
        const key = [r.loader1, r.loader2].filter(Boolean).join(", ") || "—";
        if (!map[key]) map[key] = [];
        map[key].push(r);
      });
      return Object.entries(map).map(([key, items]) => ({
        group: key,
        items,
      }));
    }
    return [{ group: "Маршруты", items: sorted }];
  }, [sorted, groupBy]);

  // === Компонент ===
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        padding: 16,
        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
      }}
    >
      <h3 style={{ marginTop: 0 }}>Эффективность сотрудников</h3>
      <p style={{ color: "#666", marginBottom: 10 }}>
        Анализ выполнения заказов по маршрутам, водителям и грузчикам.
      </p>

      {/* === Панель фильтров === */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
          style={{
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid #ccc",
            fontSize: 13,
          }}
        >
          <option value="efficiency">По эффективности</option>
          <option value="date">По дате маршрута</option>
          <option value="total">По количеству заказов</option>
          <option value="driver">По водителю</option>
        </select>

        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
          style={{
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid #ccc",
            fontSize: 13,
          }}
        >
          <option value="route">По маршрутам</option>
          <option value="driver">По водителям</option>
          <option value="loaders">По грузчикам</option>
        </select>

        <label style={{ display: "flex", alignItems: "center", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={useRealScale}
            onChange={(e) => setUseRealScale(e.target.checked)}
            style={{ marginRight: 6 }}
          />
          В реальном масштабе
        </label>

        <button
          onClick={() => {
            setGroupBy("route");
            setSortKey("efficiency");
            setUseRealScale(false);
          }}
          style={{
            padding: "6px 12px",
            borderRadius: 6,
            border: "1px solid #ccc",
            background: "#f8f8f8",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Сбросить
        </button>
      </div>

      {/* === Группы === */}
      {grouped.map((group, gi) => (
        <div key={gi} style={{ marginBottom: 20 }}>
          <h4 style={{ margin: "6px 0", color: "#444" }}>{group.group}</h4>
          <div
            style={{
              height: 260,
              display: "flex",
              alignItems: "flex-end",
              gap: 10,
              borderBottom: "1px solid #eee",
              paddingBottom: 6,
              overflowX: "auto",
            }}
          >
            {group.items.map((r, i) => {
              const baseHeight = useRealScale
                ? (r.total / maxOrders) * 180
                : 180;
              const deliveredPct = (r.delivered / r.total) * 100;
              const openPct = (r.open / r.total) * 100;
              const failedPct = (r.failed / r.total) * 100;

              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: 60,
                    minWidth: 60,
                    textAlign: "center",
                  }}
                >
                  {/* === Количество заказов сверху === */}
                  <small
                    style={{
                      fontSize: 11,
                      color: "#333",
                      marginBottom: 4,
                    }}
                  >
                    {r.total}
                  </small>

                  <div
                    style={{
                      height: baseHeight,
                      width: "100%",
                      background: "#eee",
                      borderRadius: 4,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column-reverse",
                      position: "relative",
                    }}
                    title={`Маршрут ${r.route}
                  Водитель: ${r.driver}
                  Грузчики: ${[r.loader1, r.loader2].filter(Boolean).join(", ") || "—"}
                  Дата: ${r.date || "—"}
                  Закрыто: ${r.delivered}
                  Открыто: ${r.open}
                  Не доставлено: ${r.failed}
                  (${r.efficiency.toFixed(1)}%)`}
                  >
                    {/* Цветовые полосы */}
                    <div
                      style={{
                        height: `${deliveredPct}%`,
                        background: "rgb(46, 204, 113)",
                        transition: "height 0.3s",
                      }}
                    />
                    <div
                      style={{
                        height: `${failedPct}%`,
                        background: "rgb(231, 76, 60)",
                        transition: "height 0.3s",
                      }}
                    />
                    <div
                      style={{
                        height: `${openPct}%`,
                        background: "rgb(0, 125, 255)",
                        transition: "height 0.3s",
                      }}
                    />

                    {/* === Дата маршрута внутри столбика === */}
                  {r.date && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: 10,
                        left: 0,
                        width: "100%",
                        textAlign: "center",
                        fontSize: 11,
                        color: "#fff",
                        fontWeight: 600,
                        textShadow: `
                          -1px -1px 0 #000,
                          1px -1px 0 #000,
                          -1px  1px 0 #000,
                          1px  1px 0 #000
                        `,
                        pointerEvents: "none",
                      }}
                    >
                      {new Date(r.date).toLocaleDateString("ru-RU", {
                        day: "numeric",
                        month: "long",
                      })}
                    </div>
                  )}
                  </div>

                  <small
                    style={{
                      fontSize: 11,
                      color: "#333",
                      marginTop: 4,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                    }}
                  >
                    {r.route}
                  </small>
                  <small
                    style={{
                      fontSize: 11,
                      color: "#555",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                    }}
                  >
                    {r.driver}
                  </small>
                  <small
                    style={{
                      fontSize: 11,
                      color: "#999",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                    }}
                  >
                    {[r.loader1, r.loader2].filter(Boolean).join(", ") || "—"}
                  </small>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
